import React from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const About = () => {
  return (
    <div className="about-page">
      <div className="about-container">
        <Link to="/play" className="back-link">
          ← Back to Game
        </Link>

        <h1 className="about-title">About Carnivores Adapting Terribly</h1>

        <div className="about-content">
          <section className="about-section">
            <h2>What Is This?</h2>
            <p>
              Carnivores Adapting Terribly is an online multiplayer party game for adults who enjoy
              dark humour and questionable life choices. Think Cards Against Humanity, but built for
              the modern web with actual care for user experience.
            </p>
            <p>
              This project was inspired by games we've loved over the years: Apples to Apples (which
              we first played in 2011), Cards Against Humanity (discovered in 2014), and online
              alternatives like Bad.cards and Evil Apples. Each taught us something about what works
              - and what doesn't - in digital party games.
            </p>
          </section>

          <section className="about-section">
            <h2>Why We Built This</h2>
            <p>
              After years of playing Cards Against Humanity online, we kept hitting the same
              frustrations:
            </p>
            <ul>
              <li>
                <strong>Clunky interfaces:</strong> Slow, buggy, confusing designs that kill the fun
              </li>
              <li>
                <strong>Overcrowded rooms:</strong> 30+ player games where you wait forever for your
                turn
              </li>
              <li>
                <strong>Intrusive ads:</strong> Banner ads interrupting gameplay every 30 seconds
              </li>
              <li>
                <strong>Mobile nightmares:</strong> Interfaces that barely function on phones
              </li>
              <li>
                <strong>Public chaos:</strong> Random strangers, trolls, and inappropriate content
                from people you didn't invite
              </li>
            </ul>
            <p>
              We wanted to build something better. A game that's smooth, fast, and private by
              default.
            </p>
          </section>

          <section className="about-section">
            <h2>What Makes Us Different</h2>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">🎮</div>
                <h3>Modern & Fast</h3>
                <p>
                  Real-time WebSocket multiplayer. No refresh button needed. No lag. Just smooth
                  gameplay.
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🌐</div>
                <h3>Cross-Platform</h3>
                <p>
                  Works seamlessly on desktop, mobile, and tablet. Responsive design that actually
                  functions on any screen size.
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🚫</div>
                <h3>Ad-Free</h3>
                <p>No banner ads. No interruptions. Just gameplay.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🔒</div>
                <h3>Invite-Only</h3>
                <p>
                  Private rooms by default. Share codes with friends, not random internet strangers.
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🤖</div>
                <h3>Bot Players</h3>
                <p>
                  Not enough friends online? Add bots to fill out your game. Perfect for smaller
                  groups.
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">⚡</div>
                <h3>Smart Timers</h3>
                <p>Optional timing system that only pressures slow players, not everyone.</p>
              </div>
            </div>
          </section>

          <section className="about-section">
            <h2>The Tech Behind It</h2>
            <p>For the tech-curious, here's what powers Carnivores Adapting Terribly:</p>
            <ul>
              <li>
                <strong>Frontend:</strong> React with real-time WebSocket connections
              </li>
              <li>
                <strong>Backend:</strong> Node.js with Socket.io for multiplayer
              </li>
              <li>
                <strong>Hosting:</strong> Vercel for frontend, auto-deployed from GitHub
              </li>
              <li>
                <strong>Design:</strong> Custom CSS with glassmorphism and gradient themes
              </li>
              <li>
                <strong>Security:</strong> Enterprise-grade rate limiting and input validation
              </li>
            </ul>
            <p>
              Open to feedback, feature requests, and bug reports. This is actively developed and
              improving constantly.
            </p>
          </section>

          <section className="about-section">
            <h2>Legal Stuff</h2>
            <p>
              Cards Against Humanity® is a trademark of Cards Against Humanity LLC. We're not
              affiliated with, endorsed by, or sponsored by Cards Against Humanity LLC. This is an
              independent game inspired by the original concept, using our own original card
              content.
            </p>
            <p>
              This game is for adults 18+ only. Content is offensive, inappropriate, and politically
              incorrect by design. If you're easily offended, this isn't the game for you.
            </p>
          </section>

          <section className="about-section">
            <h2>Get In Touch</h2>
            <p>
              Have questions? Found a bug? Want to share feedback?{' '}
              <Link to="/contact">Use our contact form</Link>.
            </p>
            <p className="tagline">
              Built with questionable humor and surprisingly decent code. Enjoy responsibly (or
              not).
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default About;
